import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  CalendarOff, CalendarPlus, Clock, Loader2, Pencil, Plus, RotateCcw, Settings2, Trash2,
} from 'lucide-react';
import {
  listAgendas,
  listConceptos,
  createConcepto,
  updateConcepto,
  deleteConcepto,
  listBloqueos,
  deleteBloqueo,
  todayStr,
  addDaysStr,
  strToDate,
} from '../lib/citasApi';
import { PageHeader } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import AgendaConfigDialog from '../components/citas/AgendaConfigDialog';
import BloqueoDialog from '../components/citas/BloqueoDialog';

/**
 * Gestión de agendas (solo admin): horarios semanales de cada agenda,
 * cierres/aperturas extraordinarios y catálogo de conceptos de cita.
 *
 * Reutiliza AgendaConfigDialog y BloqueoDialog de la vista de citas.
 */

const DAY_SHORT = ['L', 'M', 'X', 'J', 'V', 'S', 'D']; // 0 = lunes (criterio backend)

/** Agrupa días consecutivos con el mismo horario: [{label:"L-V", text:"09:00–14:00 / 15:00–21:00", closed}] */
function scheduleGroups(workingHours) {
  const sig = (i) =>
    ((workingHours || {})[String(i)] || []).map((r) => `${r.start}–${r.end}`).join(' / ');
  const groups = [];
  for (let i = 0; i < 7; i++) {
    const s = sig(i);
    const last = groups[groups.length - 1];
    if (last && last.sig === s) last.to = i;
    else groups.push({ from: i, to: i, sig: s });
  }
  return groups.map((g) => ({
    label: g.from === g.to ? DAY_SHORT[g.from] : `${DAY_SHORT[g.from]}-${DAY_SHORT[g.to]}`,
    text: g.sig || 'Cerrado',
    closed: !g.sig,
  }));
}

/** "YYYY-MM-DD" → "dd/mm/yyyy" (local, sin sorpresas UTC). */
function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return strToDate(dateStr).toLocaleDateString('es-ES');
}

/** Orden del catálogo: sort_order y, a igualdad, nombre. */
const ordenarConceptos = (lista) => lista
  .slice()
  .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.name).localeCompare(String(b.name), 'es'));

/** Nombre → slug para el code del concepto ("Consulta previa" → "consulta-previa"). */
function slugify(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function AgendaPill({ agenda, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`gestion-agendas-pill-${agenda.code || agenda.id}`}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm whitespace-nowrap transition-colors ${
        selected
          ? 'bg-[#2E6E5E] text-white border-[#2E6E5E] font-medium'
          : 'bg-white text-[#13231E] border-[#dbe3ee] hover:border-[#2E6E5E] hover:bg-[rgba(46,110,94,0.06)]'
      }`}
    >
      <span
        className="w-2.5 h-2.5 rounded-full shrink-0"
        style={{ backgroundColor: agenda.color || '#2E6E5E' }}
        aria-hidden="true"
      />
      {agenda.name}
    </button>
  );
}

function SectionTitle({ title, note, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
      <div>
        <h2 className="font-display text-lg font-semibold text-[#13231E]">{title}</h2>
        {note && <p className="text-xs text-muted-foreground mt-0.5">{note}</p>}
      </div>
      {action && <div className="flex flex-wrap gap-2">{action}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Diálogo de creación/edición de concepto de cita
// ---------------------------------------------------------------------------

// Las MARCAS del concepto (services/cita_conceptos.py en el servidor): qué
// reglas le tocan a sus citas. Son de cada clínica y se guardan con el
// concepto; uno sin marcar se comporta como «no».
const MARCAS_CONCEPTO = [
  { clave: 'es_cirugia', etiqueta: 'Es una intervención',
    ayuda: 'Al marcarla «En recuperación» o «Se fue» se abre sola un episodio en la ficha.' },
  { clave: 'es_retoque', etiqueta: 'Es un retoque',
    ayuda: 'Confirmación y recordatorio de retoque, y la guía del paciente sin pruebas, pauta ni pago.' },
  { clave: 'cirugia_menor', etiqueta: 'Es una cirugía menor',
    ayuda: 'Sus citas llevan el consentimiento de cirugía menor y sus instrucciones aunque la nota no lo diga.' },
  { clave: 'primera_valoracion', etiqueta: 'Es una primera valoración',
    ayuda: 'La primera visita de un paciente nuevo: cuenta en los indicadores, se marca el IVA y deja preparado el presupuesto.' },
  { clave: 'primera_revision', etiqueta: 'Es una primera revisión',
    ayuda: 'La primera revisión tras operar: también deja preparado el presupuesto si el evolutivo trae cirugía y precio.' },
  { clave: 'perfil_enfermeria', etiqueta: 'La ve enfermería',
    ayuda: 'Sus citas salen en la agenda del día de enfermería aunque la nota no diga «previa» ni «primera».' },
  { clave: 'solo_medico', etiqueta: 'Siempre del médico',
    ayuda: 'Sus citas nunca van a enfermería, diga lo que diga la nota.' },
];
const SIN_MARCAS = Object.fromEntries(MARCAS_CONCEPTO.map(({ clave }) => [clave, false]));

function ConceptoDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [category, setCategory] = useState('consulta');
  const [minutes, setMinutes] = useState('15');
  const [color, setColor] = useState('#2E6E5E');
  const [sortOrder, setSortOrder] = useState('0');
  const [marcas, setMarcas] = useState(SIN_MARCAS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || '');
    setCode(initial?.code || '');
    setCodeTouched(!!initial?.id);
    setCategory(initial?.category || 'consulta');
    setMinutes(String(initial?.default_minutes ?? 15));
    setColor(initial?.color || '#2E6E5E');
    setSortOrder(String(initial?.sort_order ?? 0));
    setMarcas(Object.fromEntries(MARCAS_CONCEPTO.map(({ clave }) => [clave, initial?.[clave] === true])));
    setSaving(false);
  }, [open, initial]);

  // Un concepto NUEVO de categoría «Cirugía» nace marcado como intervención
  // (se puede desmarcar); editar uno no toca sus marcas.
  const onChangeCategory = (v) => {
    setCategory(v);
    if (!isEdit && v === 'cirugia') setMarcas((m) => ({ ...m, es_cirugia: true }));
  };

  const onChangeName = (v) => {
    setName(v);
    if (!isEdit && !codeTouched) setCode(slugify(v));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('El nombre del concepto es obligatorio');
    if (!isEdit && !slugify(code)) return toast.error('El código (slug) es obligatorio');
    const mins = Number(minutes);
    if (!Number.isFinite(mins) || mins <= 0) return toast.error('La duración debe ser un número de minutos mayor que 0');
    const order = Number(sortOrder);
    if (!Number.isFinite(order)) return toast.error('El orden debe ser un número');
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        category,
        default_minutes: mins,
        color,
        sort_order: order,
        ...marcas,
      };
      if (isEdit) {
        await updateConcepto(initial.id, payload);
        toast.success('Concepto actualizado');
      } else {
        await createConcepto({ ...payload, code: slugify(code) });
        toast.success('Concepto creado');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar el concepto');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="concepto-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar concepto' : 'Nuevo concepto de cita'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Nombre</Label>
            <Input
              value={name}
              onChange={(e) => onChangeName(e.target.value)}
              placeholder="Ej.: Consulta previa"
              className="mt-1.5"
              data-testid="concepto-name"
            />
          </div>

          <div>
            <Label>Código (slug)</Label>
            <Input
              value={code}
              disabled={isEdit}
              onChange={(e) => {
                setCodeTouched(true);
                setCode(slugify(e.target.value));
              }}
              placeholder="consulta-previa"
              className="mt-1.5 font-mono text-sm"
              data-testid="concepto-code"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {isEdit ? 'El código no se puede cambiar una vez creado.' : 'Identificador único; se genera solo a partir del nombre.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Categoría</Label>
              <Select value={category} onValueChange={onChangeCategory}>
                <SelectTrigger className="mt-1.5" data-testid="concepto-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="consulta">Consulta</SelectItem>
                  <SelectItem value="cirugia">Cirugía</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duración (min)</Label>
              <Input
                type="number"
                min={5}
                step={5}
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                className="mt-1.5 tabular-nums"
                data-testid="concepto-minutes"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Color</Label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="mt-1.5 block h-10 w-14 rounded-md border border-input bg-white p-1 cursor-pointer"
                aria-label="Color del concepto"
                data-testid="concepto-color"
              />
            </div>
            <div>
              <Label>Orden</Label>
              <Input
                type="number"
                min={0}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="mt-1.5 tabular-nums"
                data-testid="concepto-sort-order"
              />
            </div>
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Qué es este tipo de cita</legend>
            {MARCAS_CONCEPTO.map(({ clave, etiqueta, ayuda }) => (
              <label key={clave} className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={!!marcas[clave]}
                  onChange={(e) => setMarcas((m) => ({ ...m, [clave]: e.target.checked }))}
                  data-testid={`concepto-marca-${clave}`}
                />
                <span>
                  {etiqueta}
                  <span className="block text-xs text-muted-foreground">{ayuda}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="concepto-cancel">
              Cancelar
            </Button>
            <Button type="submit" disabled={saving} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="concepto-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEdit ? 'Guardar cambios' : 'Crear concepto'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function GestionAgendasPage() {
  const [agendas, setAgendas] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sección agendas
  const [configAgenda, setConfigAgenda] = useState(null);

  // Sección cierres/aperturas
  const [selAgendaId, setSelAgendaId] = useState(null);
  const [bloqueos, setBloqueos] = useState([]);
  const [loadingBloqueos, setLoadingBloqueos] = useState(false);
  const [bloqueoTipo, setBloqueoTipo] = useState(null); // 'cierre' | 'apertura' | null
  const [deletingBloqueo, setDeletingBloqueo] = useState(null);
  const [deletingBloqueoBusy, setDeletingBloqueoBusy] = useState(false);

  // Sección conceptos
  const [conceptoDialogOpen, setConceptoDialogOpen] = useState(false);
  const [editingConcepto, setEditingConcepto] = useState(null);
  const [deactivatingConcepto, setDeactivatingConcepto] = useState(null);
  const [deactivatingBusy, setDeactivatingBusy] = useState(false);
  const [reactivatingId, setReactivatingId] = useState(null);

  const selAgenda = useMemo(
    () => agendas.find((a) => a.id === selAgendaId) || null,
    [agendas, selAgendaId],
  );

  // El catálogo se carga CON los retirados (revisión 2026-09-03): se listan
  // atenuados y con «Reactivar», para que retirar un tipo («Cirugía» y
  // «Cirugía menor» en el punto 4) no sea irreversible desde la pantalla.
  // Activos primero; el diálogo de duraciones por agenda solo ve los activos.
  const activeConceptos = useMemo(() => ordenarConceptos(conceptos.filter((c) => c.active !== false)), [conceptos]);
  const conceptosListado = useMemo(
    () => [...activeConceptos, ...ordenarConceptos(conceptos.filter((c) => c.active === false))],
    [conceptos, activeConceptos],
  );

  const reloadAgendas = useCallback(async () => {
    const ags = await listAgendas();
    const list = Array.isArray(ags) ? ags : [];
    setAgendas(list);
    if (list.length > 0) setSelAgendaId((prev) => (prev && list.some((a) => a.id === prev) ? prev : list[0].id));
    return list;
  }, []);

  const reloadConceptos = useCallback(async () => {
    const cons = await listConceptos({ todos: true });
    setConceptos(Array.isArray(cons) ? cons : []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([listAgendas(), listConceptos({ todos: true })])
      .then(([ags, cons]) => {
        if (cancelled) return;
        const list = Array.isArray(ags) ? ags : [];
        setAgendas(list);
        setConceptos(Array.isArray(cons) ? cons : []);
        if (list.length > 0) setSelAgendaId((prev) => prev || list[0].id);
      })
      .catch((e) => {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'No se pudieron cargar las agendas');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Bloqueos de la agenda seleccionada: desde hoy hasta +365 días.
  const reloadBloqueos = useCallback(async () => {
    if (!selAgendaId) return;
    setLoadingBloqueos(true);
    try {
      const hoy = todayStr();
      const bs = await listBloqueos(selAgendaId, { dateFrom: hoy, dateTo: addDaysStr(hoy, 365) });
      setBloqueos(Array.isArray(bs) ? bs : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron cargar los cierres y aperturas');
    } finally {
      setLoadingBloqueos(false);
    }
  }, [selAgendaId]);

  useEffect(() => {
    reloadBloqueos();
  }, [reloadBloqueos]);

  const confirmDeleteBloqueo = async () => {
    if (!deletingBloqueo) return;
    setDeletingBloqueoBusy(true);
    try {
      await deleteBloqueo(deletingBloqueo.id);
      toast.success(deletingBloqueo.tipo === 'apertura' ? 'Apertura eliminada' : 'Cierre eliminado');
      setDeletingBloqueo(null);
      reloadBloqueos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    } finally {
      setDeletingBloqueoBusy(false);
    }
  };

  // Reactivar un tipo retirado: PUT del concepto con active=true (el mismo
  // endpoint de edición; el catálogo se recarga con el flag nuevo).
  const reactivateConcepto = async (c) => {
    setReactivatingId(c.id);
    try {
      await updateConcepto(c.id, { active: true });
      toast.success(`«${c.name}» vuelve a estar disponible`);
      await reloadConceptos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo reactivar el concepto');
    } finally {
      setReactivatingId(null);
    }
  };

  const confirmDeactivateConcepto = async () => {
    if (!deactivatingConcepto) return;
    setDeactivatingBusy(true);
    try {
      await deleteConcepto(deactivatingConcepto.id);
      toast.success('Concepto desactivado');
      setDeactivatingConcepto(null);
      reloadConceptos();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo desactivar el concepto');
    } finally {
      setDeactivatingBusy(false);
    }
  };

  const openNewConcepto = () => {
    setEditingConcepto(null);
    setConceptoDialogOpen(true);
  };
  const openEditConcepto = (c) => {
    setEditingConcepto(c);
    setConceptoDialogOpen(true);
  };

  return (
    <div data-testid="gestion-agendas-page">
      <PageHeader title="Gestión de agendas" subtitle="Horarios, cierres, aperturas y conceptos de cita" />

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* ------------------------------------------------------------ */}
          {/* Agendas                                                       */}
          {/* ------------------------------------------------------------ */}
          <section data-testid="gestion-agendas-section-agendas">
            <SectionTitle title="Agendas" note="Horario semanal, tamaño de hueco y duraciones por concepto de cada agenda." />
            {agendas.length === 0 ? (
              <div className="cp-card p-8 text-center text-sm text-muted-foreground">
                No hay agendas configuradas.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {agendas.map((a) => {
                  const overridesCount = Object.keys(a.concept_overrides || {}).length;
                  return (
                    <div key={a.id} className="cp-card overflow-hidden flex flex-col" data-testid={`gestion-agendas-card-${a.code || a.id}`}>
                      <div className="h-1.5" style={{ backgroundColor: a.color || '#2E6E5E' }} aria-hidden="true" />
                      <div className="p-4 flex-1 flex flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-display font-semibold text-[#13231E]">{a.name}</div>
                            {a.doctor_name && <div className="text-xs text-muted-foreground mt-0.5">{a.doctor_name}</div>}
                          </div>
                          <Badge variant="outline" className="shrink-0 text-[11px] tabular-nums gap-1">
                            <Clock className="w-3 h-3" /> Hueco: {a.slot_minutes || 15} min
                          </Badge>
                        </div>

                        <div className="mt-3 space-y-1 text-sm flex-1">
                          {scheduleGroups(a.working_hours).map((g, i) => (
                            <div key={i} className="flex gap-2">
                              <span className="w-9 shrink-0 font-medium text-[#13231E] tabular-nums">{g.label}</span>
                              <span className={g.closed ? 'text-muted-foreground/70' : 'text-foreground tabular-nums'}>
                                {g.text}
                              </span>
                            </div>
                          ))}
                        </div>

                        {overridesCount > 0 && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            {overridesCount} {overridesCount === 1 ? 'duración personalizada' : 'duraciones personalizadas'}
                          </div>
                        )}

                        <div className="mt-3 pt-3 border-t border-[#F0F6F2]">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfigAgenda(a)}
                            data-testid={`gestion-agendas-config-${a.code || a.id}`}
                          >
                            <Settings2 className="w-4 h-4 mr-1.5" /> Configurar
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* ------------------------------------------------------------ */}
          {/* Cierres y aperturas                                           */}
          {/* ------------------------------------------------------------ */}
          <section data-testid="gestion-agendas-section-bloqueos">
            <SectionTitle
              title="Cierres y aperturas"
              note="Cierra días u horas concretas (vacaciones, congresos…) o abre tramos extraordinarios fuera del horario semanal."
              action={
                <>
                  <Button
                    variant="outline"
                    className="h-9"
                    disabled={!selAgenda}
                    onClick={() => setBloqueoTipo('cierre')}
                    data-testid="gestion-agendas-new-cierre"
                  >
                    <CalendarOff className="w-4 h-4 mr-1.5" /> Cerrar días/huecos
                  </Button>
                  <Button
                    variant="outline"
                    className="h-9"
                    disabled={!selAgenda}
                    onClick={() => setBloqueoTipo('apertura')}
                    data-testid="gestion-agendas-new-apertura"
                  >
                    <CalendarPlus className="w-4 h-4 mr-1.5" /> Abrir huecos extra
                  </Button>
                </>
              }
            />

            {agendas.length > 1 && (
              <div className="flex items-center gap-1.5 overflow-x-auto max-w-full mb-3" data-testid="gestion-agendas-bloqueos-pills">
                {agendas.map((a) => (
                  <AgendaPill key={a.id} agenda={a} selected={a.id === selAgendaId} onClick={() => setSelAgendaId(a.id)} />
                ))}
              </div>
            )}

            {loadingBloqueos ? (
              <div className="cp-card p-8 text-center text-sm text-muted-foreground">Cargando…</div>
            ) : bloqueos.length === 0 ? (
              <div className="cp-card p-8 text-center" data-testid="gestion-agendas-bloqueos-empty">
                <CalendarOff className="w-8 h-8 text-[#2E6E5E] mx-auto mb-2" />
                <div className="text-sm font-medium text-[#13231E]">Sin cierres ni aperturas programados</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {selAgenda ? `La agenda ${selAgenda.name} no tiene cierres ni aperturas en los próximos 12 meses.` : ''}
                </p>
              </div>
            ) : (
              <div className="cp-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F0F6F2] border-b border-[#D9E3DD]">
                      <tr className="text-left text-xs uppercase tracking-wider text-[#55677a]">
                        <th className="py-3 px-4">Tipo</th>
                        <th className="py-3 px-4">Fechas</th>
                        <th className="py-3 px-4">Horario</th>
                        <th className="py-3 px-4">Motivo</th>
                        <th className="py-3 px-4 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D9E3DD]">
                      {bloqueos.map((b) => (
                        <tr key={b.id} className="hover:bg-[rgba(46,110,94,0.05)]" data-testid={`gestion-agendas-bloqueo-row-${b.id}`}>
                          <td className="py-3 px-4">
                            {b.tipo === 'apertura' ? (
                              <Badge variant="outline" className="bg-[rgba(22,163,74,0.08)] text-green-700 border-green-200">
                                Apertura
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-[rgba(220,38,38,0.08)] text-red-700 border-red-200">
                                Cierre
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm tabular-nums">
                            {b.date_from === b.date_to ? fmtDate(b.date_from) : `${fmtDate(b.date_from)} — ${fmtDate(b.date_to)}`}
                          </td>
                          <td className="py-3 px-4 text-sm tabular-nums">
                            {b.all_day ? 'Día completo' : `${b.start || ''}–${b.end || ''}`}
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {b.reason || <span className="text-muted-foreground">—</span>}
                            {b.created_by_name && (
                              <div className="text-[11px] text-muted-foreground mt-0.5">{b.created_by_name}</div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive"
                              onClick={() => setDeletingBloqueo(b)}
                              aria-label="Eliminar"
                              data-testid={`gestion-agendas-bloqueo-delete-${b.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          {/* ------------------------------------------------------------ */}
          {/* Conceptos de cita                                             */}
          {/* ------------------------------------------------------------ */}
          <section data-testid="gestion-agendas-section-conceptos">
            <SectionTitle
              title="Conceptos de cita"
              note="Las cirugías también se dan como citas: crea aquí sus conceptos y duración."
              action={
                <Button onClick={openNewConcepto} className="h-9 bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="concepto-nuevo">
                  <Plus className="w-4 h-4 mr-1.5" /> Nuevo concepto
                </Button>
              }
            />

            {conceptosListado.length === 0 ? (
              <div className="cp-card p-8 text-center text-sm text-muted-foreground">
                No hay conceptos de cita. Crea el primero con “Nuevo concepto”.
              </div>
            ) : (
              <div className="cp-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F0F6F2] border-b border-[#D9E3DD]">
                      <tr className="text-left text-xs uppercase tracking-wider text-[#55677a]">
                        <th className="py-3 px-4">Nombre</th>
                        <th className="py-3 px-4">Categoría</th>
                        <th className="py-3 px-4">Duración por defecto</th>
                        <th className="py-3 px-4">Orden</th>
                        <th className="py-3 px-4 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D9E3DD]">
                      {conceptosListado.map((c) => (
                        <tr
                          key={c.id}
                          className={`hover:bg-[rgba(46,110,94,0.05)] ${c.active === false ? 'opacity-60' : ''}`}
                          data-testid={`gestion-agendas-concepto-row-${c.code || c.id}`}
                          data-retirado={c.active === false ? '1' : undefined}
                        >
                          <td className="py-3 px-4 text-sm">
                            <div className="flex items-center gap-2.5">
                              <span
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: c.color || '#2E6E5E' }}
                                aria-hidden="true"
                              />
                              <span className="font-medium text-[#13231E]">{c.name}</span>
                              {c.active === false && (
                                <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-muted-foreground" title="Ya no se ofrece al dar citas; las citas que lo llevan siguen igual">
                                  Retirado
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {c.category === 'cirugia' ? (
                              <Badge className="bg-[#2E6E5E] text-white border-[#2E6E5E] hover:bg-[#2E6E5E]">Cirugía</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-[rgba(46,110,94,0.12)] text-[#13231E] border-[rgba(46,110,94,0.28)]">
                                Consulta
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm tabular-nums">{c.default_minutes} min</td>
                          <td className="py-3 px-4 text-sm tabular-nums text-muted-foreground">{c.sort_order ?? 0}</td>
                          <td className="py-3 px-4 text-right whitespace-nowrap">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditConcepto(c)}
                              aria-label={`Editar ${c.name}`}
                              data-testid={`gestion-agendas-concepto-edit-${c.code || c.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {c.active === false ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-[#2E6E5E] border-[#2E6E5E]/40 hover:bg-[#2E6E5E]/10"
                                onClick={() => reactivateConcepto(c)}
                                disabled={reactivatingId === c.id}
                                title="Volver a ofrecerlo al dar citas"
                                data-testid={`gestion-agendas-concepto-reactivar-${c.code || c.id}`}
                              >
                                {reactivatingId === c.id
                                  ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  : <RotateCcw className="w-4 h-4 mr-1" />}
                                Reactivar
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => setDeactivatingConcepto(c)}
                                aria-label={`Desactivar ${c.name}`}
                                data-testid={`gestion-agendas-concepto-delete-${c.code || c.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Diálogos                                                          */}
      {/* ---------------------------------------------------------------- */}

      <AgendaConfigDialog
        open={!!configAgenda}
        onOpenChange={(v) => {
          if (!v) setConfigAgenda(null);
        }}
        agenda={configAgenda}
        conceptos={activeConceptos}
        onSaved={() => {
          reloadAgendas().catch(() => {});
        }}
      />

      <BloqueoDialog
        open={!!bloqueoTipo}
        onOpenChange={(v) => {
          if (!v) setBloqueoTipo(null);
        }}
        agenda={selAgenda}
        defaultDate={todayStr()}
        defaultTipo={bloqueoTipo || 'cierre'}
        onSaved={reloadBloqueos}
      />

      <AlertDialog
        open={!!deletingBloqueo}
        onOpenChange={(v) => {
          if (!v) setDeletingBloqueo(null);
        }}
      >
        <AlertDialogContent data-testid="gestion-agendas-bloqueo-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deletingBloqueo?.tipo === 'apertura' ? '¿Eliminar esta apertura?' : '¿Eliminar este cierre?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deletingBloqueo
                ? `${deletingBloqueo.tipo === 'apertura' ? 'Apertura' : 'Cierre'} del ${
                    deletingBloqueo.date_from === deletingBloqueo.date_to
                      ? fmtDate(deletingBloqueo.date_from)
                      : `${fmtDate(deletingBloqueo.date_from)} al ${fmtDate(deletingBloqueo.date_to)}`
                  }${deletingBloqueo.reason ? ` · ${deletingBloqueo.reason}` : ''}. Esta acción no se puede deshacer.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deletingBloqueoBusy}
              onClick={(e) => {
                e.preventDefault();
                confirmDeleteBloqueo();
              }}
              data-testid="gestion-agendas-bloqueo-delete-confirm-btn"
            >
              {deletingBloqueoBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConceptoDialog
        open={conceptoDialogOpen}
        onOpenChange={setConceptoDialogOpen}
        initial={editingConcepto}
        onSaved={reloadConceptos}
      />

      <AlertDialog
        open={!!deactivatingConcepto}
        onOpenChange={(v) => {
          if (!v) setDeactivatingConcepto(null);
        }}
      >
        <AlertDialogContent data-testid="gestion-agendas-concepto-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desactivar “{deactivatingConcepto?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              El concepto dejará de estar disponible al crear nuevas citas. Las citas existentes no se ven afectadas y podrás reactivarlo desde esta misma lista.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deactivatingBusy}
              onClick={(e) => {
                e.preventDefault();
                confirmDeactivateConcepto();
              }}
              data-testid="gestion-agendas-concepto-delete-confirm-btn"
            >
              {deactivatingBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Desactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
